import React from 'react';
import { HelpCircle } from 'lucide-react';

const CommonQuestionsPage = () => {
  const questions = [
    {
      question: "What does \"watch and wait\" actually mean?",
      answer: "Watch and wait is a monitoring approach where your healthcare team tracks your blood results and symptoms over time without immediate treatment. It's used when CLL is present but not causing significant problems. Many people find this period challenging emotionally, even when it's the medically appropriate approach. It's a time of observation, not inaction."
    },
    {
      question: "Why do blood numbers fluctuate?",
      answer: "Blood results can vary from test to test due to many factors — hydration, time of day, recent illness, stress, or even lab variance. This is why doctors look at trends over months and years rather than reacting to single results. One result that's slightly outside the reference range doesn't necessarily signal a problem."
    },
    {
      question: "Why do doctors focus on trends, not single results?",
      answer: "CLL progression is typically gradual. Your healthcare team is watching for patterns — are lymphocytes steadily rising? Is haemoglobin consistently dropping? A single \"off\" result in isolation rarely changes clinical decisions. Context and direction matter more than individual numbers."
    },
    {
      question: "What are reference ranges, and why do they vary?",
      answer: "Reference ranges represent what's typical for most healthy people, but they can vary between labs and countries. They're guidelines, not absolute boundaries. Your doctor interprets your results in the context of your history, symptoms, and overall clinical picture — not just whether a number is inside or outside a printed range."
    },
    {
      question: "Why do some people explore nutrition or lifestyle topics?",
      answer: "Many people want to feel like they're doing something supportive during watch and wait. While there's no specific diet or lifestyle change proven to alter CLL progression, some find exploring whole foods, stress management, or gentle movement helps them feel more in control. It's about personal wellbeing, not medical intervention."
    },
    {
      question: "What questions might I want to ask at my next appointment?",
      answer: "Consider asking about what markers your doctor is watching most closely, what would trigger a change in monitoring frequency, and what symptoms to report between appointments. Some people also ask about clinical trials, lifestyle considerations, or what to expect in future stages of monitoring."
    },
    {
      question: "Why does my doctor check specific markers more than others?",
      answer: "In CLL, certain markers like absolute lymphocyte count, haemoglobin, and platelet levels often provide the most insight into disease activity. Your doctor uses these alongside physical examination findings (like lymph node size) to assess progression. Not every test is equally relevant for CLL monitoring."
    },
    {
      question: "What does \"indolent\" mean?",
      answer: "Indolent means slow-growing. CLL is often described as an indolent cancer because it typically progresses gradually over years. This is why watch and wait is common — many people live for years or decades with CLL without needing treatment. Indolent doesn't mean harmless, but it does mean slow."
    },
    {
      question: "Why do some people's CLL never needs treatment?",
      answer: "CLL behaves differently in different people. Some cases remain stable for many years without causing symptoms or health problems. Age, overall health, and the specific characteristics of your CLL all play a role. Treatment is only considered when the disease starts causing significant issues."
    },
    {
      question: "How do I know if something I'm experiencing is related to CLL?",
      answer: "Fatigue, infections, night sweats, or unexplained weight loss can be CLL-related, but they can also have other causes. This is why reporting new or changing symptoms to your healthcare team matters. They can help determine what's relevant and what's not."
    },
    {
      question: "What role does stress or anxiety play?",
      answer: "Living with CLL, especially during watch and wait, can be emotionally taxing. Anxiety doesn't cause CLL to progress, but it can affect quality of life. Many people find counselling, support groups, or mindfulness helpful. Managing your mental wellbeing is part of living with a chronic condition."
    },
    {
      question: "Is it normal to feel uncertain about watch and wait?",
      answer: "Yes. Many people find it counterintuitive to have cancer and not treat it immediately. Watch and wait is evidence-based and appropriate when treatment risks outweigh benefits, but that doesn't make it emotionally easy. Feeling uncertain, anxious, or wanting more information is completely normal."
    }
  ];

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4">
          Common Questions People with CLL Ask
        </h1>
        <p className="text-base md:text-lg leading-relaxed text-slate-700 max-w-3xl">
          These are questions many people wonder about when living with CLL. The information here provides context, not medical advice. Always discuss your specific situation with your healthcare team.
        </p>
      </div>

      {/* Medical Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
        <div className="flex items-start gap-3">
          <HelpCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-1" />
          <div>
            <p className="text-sm font-medium text-amber-900 mb-2">
              Educational Context Only
            </p>
            <p className="text-sm text-amber-800">
              This page is not medical advice. It's designed to provide context for common questions. Your healthcare team is your primary source of guidance for your specific situation.
            </p>
          </div>
        </div>
      </div>

      {/* Questions List */}
      <div className="space-y-6">
        {questions.map((item, index) => (
          <div 
            key={index}
            className="bg-white rounded-2xl p-6 md:p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
          >
            <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4 flex items-start gap-3">
              <span className="text-primary flex-shrink-0">{index + 1}.</span>
              <span>{item.question}</span>
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-slate-700 pl-8">
              {item.answer}
            </p>
          </div>
        ))}
      </div>

      {/* Bottom Reminder */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mt-12">
        <p className="text-base text-blue-900 text-center">
          <strong>Remember:</strong> These answers provide general context. Your specific situation, blood results, and symptoms should always be discussed with your healthcare team.
        </p>
      </div>
    </div>
  );
};

export default CommonQuestionsPage;
